import React from "react";

interface RouteDetailViewProps {
  routeName: string | null;
  routeDistance: number;
  isSaved: boolean;
  isRenaming: boolean;
  renameValue: string;
  onToggleSave: () => void;
  onRename: () => void;
  onRenameChange: (value: string) => void;
  onSetRenaming: (isRenaming: boolean) => void;
  onZoom: () => void;
  onStop: () => void;
  onClose: () => void;
}

export const RouteDetailView: React.FC<RouteDetailViewProps> = ({
  routeName,
  routeDistance,
  isSaved,
  isRenaming,
  renameValue,
  onToggleSave,
  onRename,
  onRenameChange,
  onSetRenaming,
  onZoom,
  onStop,
  onClose,
}) => {
  return (
    <>
      <div
        className="island-header"
        style={{ marginBottom: "4px", padding: "8px 8px 4px 8px" }}
      >
        <div className="island-header-content">
          <div className="island-icon">
            <svg width="20" height="16" viewBox="0 0 24 20" fill="none">
              <path
                d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
                stroke="#60a5fa"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <div className="island-text">
            <div className="island-title">{routeName || "Active Route"}</div>
            <div className="island-subtitle">{routeDistance.toFixed(1)} km</div>
          </div>
        </div>
        <div
          className="island-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <button
            onClick={onToggleSave}
            title={isSaved ? "Unsave route" : "Save route"}
            style={{
              background: isSaved
                ? "rgba(255, 215, 0, 0.2)"
                : "rgba(255, 255, 255, 0.1)",
              border: isSaved
                ? "1px solid rgba(255, 215, 0, 0.3)"
                : "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "4px",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              fontSize: "14px",
            }}
          >
            {isSaved ? "⭐" : "☆"}
          </button>
          <button
            onClick={onZoom}
            title="Zoom to fit route"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "4px",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              color: "var(--text-primary)",
              fontSize: "14px",
            }}
          >
            ⛶
          </button>
          <button
            onClick={onStop}
            title="Close route"
            style={{
              background: "rgba(255, 50, 50, 0.2)",
              border: "1px solid rgba(255, 50, 50, 0.3)",
              borderRadius: "4px",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              color: "#ff6b6b",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            ×
          </button>
          <button className="island-close" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className="island-details" style={{ padding: "12px" }}>
        <div
          style={{
            textAlign: "center",
            padding: "8px",
            background: "rgba(96, 165, 250, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(96, 165, 250, 0.3)",
          }}
        >
          <div
            style={{
              fontSize: "10px",
              color: "var(--text-secondary)",
              marginBottom: "4px",
            }}
          >
            Route displayed on map
          </div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#60a5fa",
            }}
          >
            {routeDistance.toFixed(2)} km
          </div>
        </div>

        {/* Rename route (only if saved) */}
        {isSaved && (
          <div style={{ marginTop: "12px" }}>
            {isRenaming ? (
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => onRenameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onRename();
                    if (e.key === "Escape") {
                      onSetRenaming(false);
                      onRenameChange("");
                    }
                  }}
                  placeholder="Route name"
                  autoFocus
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    fontSize: "11px",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    color: "var(--text-primary)",
                    outline: "none",
                  }}
                />
                <button
                  onClick={onRename}
                  className="island-btn primary"
                  style={{ padding: "6px 10px", fontSize: "10px" }}
                >
                  ✓
                </button>
                <button
                  onClick={() => {
                    onSetRenaming(false);
                    onRenameChange("");
                  }}
                  className="island-btn default"
                  style={{ padding: "6px 10px", fontSize: "10px" }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  onSetRenaming(true);
                  onRenameChange(routeName || "");
                }}
                className="island-btn default"
                style={{
                  width: "100%",
                  padding: "6px",
                  fontSize: "10px",
                }}
              >
                Rename Route
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
